import { useEffect, useState, type DragEvent } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Boxes,
  ChevronDown,
  ChevronUp,
  ClipboardCheck,
  Dna,
  Eye,
  EyeOff,
  Factory,
  FileText,
  FlaskConical,
  Folder,
  GraduationCap,
  GripVertical,
  HeartPulse,
  KeyRound,
  Microscope,
  Plug,
  Server,
  ShieldCheck,
  Star,
  Syringe,
  TestTube,
  Truck,
  type LucideIcon,
} from 'lucide-react'
import type { Product } from '@/api/directoryApi'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { arrangeProducts, EMPTY_PRODUCT_NAV, moveKey, PRODUCT_ICON_NAMES, type ProductIconName, type ProductNavPrefs } from '@/lib/productNav'
import { cn } from '@/lib/utils'

export const PRODUCT_ICONS: Record<ProductIconName, LucideIcon> = {
  dna: Dna,
  flask: FlaskConical,
  microscope: Microscope,
  'test-tube': TestTube,
  syringe: Syringe,
  'heart-pulse': HeartPulse,
  'shield-check': ShieldCheck,
  'clipboard-check': ClipboardCheck,
  'file-text': FileText,
  'graduation-cap': GraduationCap,
  plug: Plug,
  server: Server,
  key: KeyRound,
  boxes: Boxes,
  factory: Factory,
  truck: Truck,
  star: Star,
  folder: Folder,
}

/**
 * Arrange the sidebar's "By product" section for yourself: drag (or the
 * arrows) to reorder, pick an icon, hide what you don't work on. Changes are a
 * draft until Save.
 */
export function ProductNavDialog({
  open,
  products,
  prefs,
  onOpenChange,
  onSave,
}: {
  open: boolean
  products: Product[]
  prefs: ProductNavPrefs
  onOpenChange: (open: boolean) => void
  onSave: (prefs: ProductNavPrefs) => void
}) {
  const { t } = useTranslation()
  const [draft, setDraft] = useState(prefs)
  const [dragging, setDragging] = useState<number | null>(null)
  const [over, setOver] = useState<number | null>(null)
  useEffect(() => {
    if (open) setDraft(prefs)
  }, [open, prefs])

  const rows = arrangeProducts(products, draft)
  const keys = rows.map((r) => r.product.key)
  const move = (from: number, to: number) => setDraft({ ...draft, order: moveKey(keys, from, to) })
  const toggleHidden = (key: string) =>
    setDraft({ ...draft, hidden: draft.hidden.includes(key) ? draft.hidden.filter((k) => k !== key) : [...draft.hidden, key] })
  const setIcon = (key: string, icon: ProductIconName | null) => {
    const icons = { ...draft.icons }
    if (icon) icons[key] = icon
    else delete icons[key]
    setDraft({ ...draft, icons })
  }
  const drop = (e: DragEvent, to: number) => {
    e.preventDefault()
    if (dragging !== null && dragging !== to) move(dragging, to)
    setDragging(null)
    setOver(null)
  }
  const iconButton = 'rounded p-1 text-ink-muted hover:bg-surface-subtle hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-30'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-md overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('nav.productNav.title')}</DialogTitle>
          <DialogDescription>{t('nav.productNav.help')}</DialogDescription>
        </DialogHeader>

        <ul className="flex flex-col gap-1" aria-label={t('nav.byProduct')}>
          {rows.map(({ product, icon, hidden }, i) => {
            const Icon = icon ? PRODUCT_ICONS[icon] : null
            return (
              <li
                key={product.key}
                draggable
                onDragStart={(e) => {
                  setDragging(i)
                  e.dataTransfer.effectAllowed = 'move'
                }}
                onDragOver={(e) => {
                  e.preventDefault()
                  setOver(i)
                }}
                onDragLeave={() => setOver((o) => (o === i ? null : o))}
                onDrop={(e) => drop(e, i)}
                onDragEnd={() => (setDragging(null), setOver(null))}
                className={cn(
                  'flex items-center gap-2 rounded-md border border-line bg-background px-2 py-1.5 transition-colors',
                  dragging === i && 'opacity-40',
                  over === i && dragging !== null && dragging !== i && 'border-primary bg-accent',
                )}
              >
                <GripVertical className="size-4 shrink-0 cursor-grab text-ink-muted" aria-hidden />
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      aria-label={t('nav.productNav.pickIcon', { name: product.name })}
                      title={t('nav.productNav.pickIcon', { name: product.name })}
                      className="flex size-7 shrink-0 items-center justify-center rounded-md border border-dashed border-line-strong text-ink-muted hover:border-primary hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      {Icon ? <Icon className="size-4" aria-hidden /> : <span className="text-label">+</span>}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="w-56 p-2">
                    <div className="grid grid-cols-6 gap-1" role="group" aria-label={t('nav.productNav.icons')}>
                      {PRODUCT_ICON_NAMES.map((name) => {
                        const Choice = PRODUCT_ICONS[name]
                        return (
                          <button
                            key={name}
                            type="button"
                            aria-label={t(`nav.productNav.iconNames.${name}`)}
                            title={t(`nav.productNav.iconNames.${name}`)}
                            aria-pressed={icon === name}
                            onClick={() => setIcon(product.key, name)}
                            className={cn(
                              'flex size-8 items-center justify-center rounded-md text-ink-body hover:bg-surface-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                              icon === name && 'bg-accent text-primary',
                            )}
                          >
                            <Choice className="size-4" aria-hidden />
                          </button>
                        )
                      })}
                    </div>
                    {icon ? (
                      <button type="button" onClick={() => setIcon(product.key, null)} className="mt-2 w-full rounded-md px-2 py-1 text-left text-meta text-ink-muted hover:bg-surface-subtle">
                        {t('nav.productNav.noIcon')}
                      </button>
                    ) : null}
                  </PopoverContent>
                </Popover>
                <span className={cn('min-w-0 grow truncate text-body', hidden ? 'text-ink-muted line-through' : 'text-ink')}>{product.name}</span>
                <button type="button" className={iconButton} disabled={i === 0} aria-label={t('nav.productNav.up', { name: product.name })} onClick={() => move(i, i - 1)}>
                  <ChevronUp className="size-4" aria-hidden />
                </button>
                <button type="button" className={iconButton} disabled={i === rows.length - 1} aria-label={t('nav.productNav.down', { name: product.name })} onClick={() => move(i, i + 1)}>
                  <ChevronDown className="size-4" aria-hidden />
                </button>
                <button
                  type="button"
                  className={iconButton}
                  aria-pressed={!hidden}
                  aria-label={t(hidden ? 'nav.productNav.show' : 'nav.productNav.hide', { name: product.name })}
                  title={t(hidden ? 'nav.productNav.show' : 'nav.productNav.hide', { name: product.name })}
                  onClick={() => toggleHidden(product.key)}
                >
                  {hidden ? <EyeOff className="size-4" aria-hidden /> : <Eye className="size-4" aria-hidden />}
                </button>
              </li>
            )
          })}
        </ul>

        <DialogFooter className="gap-2 sm:justify-between">
          <Button variant="ghost" onClick={() => setDraft(EMPTY_PRODUCT_NAV)}>
            {t('nav.productNav.reset')}
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={() => {
                onSave(draft)
                onOpenChange(false)
              }}
            >
              {t('common.save')}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
